import React from "react";
import * as Icon from "react-feather";

import "./trial-balance-import-modal.css";
import WComponent from "../../../common/WComponent";
import ToolsSelect from "../kit/ToolsSelect";
import CoaMappingReview from "./CoaMappingReview";
import BookkeepingService from "../../../../services/BookkeepingService";
import UtilityService from "../../../../services/UtilityService";

const COLUMN_ROLES = [
	{ value: "accountNumber", label: "Account number" },
	{ value: "accountDescription", label: "Account description" },
	{ value: "debit", label: "Debit" },
	{ value: "credit", label: "Credit" },
	{ value: "amount", label: "Signed balance" },
];

class TrialBalanceImportPanel extends WComponent {
	state = {
		grid: null,
		fileName: "",
		reading: false,
		preview: null,
		error: "",
		columns: null,
		headerRowIndex: null,
		importing: false,
		imported: null,
		mappingOpen: false,
		clearing: false,
	};

	onChooseFile = async event => {
		const file = event.target.files && event.target.files[0];
		if (!file) return;

		await this.update({ reading: true, error: "", preview: null, grid: null, columns: null, headerRowIndex: null, imported: null, fileName: file.name });

		const read = await BookkeepingService.readStatementGrid({ clientId: this.props.clientId, file });
		if (read.error || !read.grid) {
			await this.update({ reading: false, error: read.error || "That file could not be read." });
			return;
		}

		await this.update({ grid: read.grid });
		await this.loadPreview({});
	};

	loadPreview = async ({ columns, headerRowIndex }) => {
		const { clientId } = this.props;
		const { grid } = this.state;
		if (!grid) return;

		await this.update({ reading: true, error: "" });
		try {
			const preview = await BookkeepingService.previewTrialBalanceImport({
				clientId,
				grid,
				headerRowIndex: headerRowIndex === undefined ? this.state.headerRowIndex : headerRowIndex,
				columns: columns === undefined ? this.state.columns : columns,
			});
			await this.update({
				reading: false,
				preview,
				columns: preview.columns,
				headerRowIndex: preview.headerRowIndex,
			});
		} catch (error) {
			await this.update({ reading: false, error: "That trial balance could not be read." });
		}
	};

	onAssignColumn = async ({ columnIndex, role }) => {
		const columns = { ...(this.state.columns || {}) };

		Object.keys(columns).forEach(assignedRole => {
			if (columns[assignedRole] === columnIndex) delete columns[assignedRole];
		});
		if (role) columns[role] = columnIndex;

		await this.update({ columns });
		await this.loadPreview({ columns });
	};

	onImport = async () => {
		const { wpfEngagementId, onImported } = this.props;
		const { preview, fileName } = this.state;

		await this.update({ importing: true, error: "" });
		try {
			const result = await BookkeepingService.commitTrialBalanceImport({
				wpfEngagementId,
				preview,
				sourceFileName: fileName,
			});
			await this.update({ importing: false, imported: result });
			if (onImported) onImported({ importedTrialBalance: result.importedTrialBalance });
		} catch (error) {
			const said = (error && error.response && error.response.data && error.response.data.error) || "";
			await this.update({ importing: false, error: said || "That trial balance could not be imported." });
		}
	};

	onClearImport = async () => {
		const { wpfEngagementId, onCleared } = this.props;

		await this.update({ clearing: true, error: "" });
		const cleared = await BookkeepingService.clearTrialBalanceImport({ wpfEngagementId });
		if (!cleared || cleared.error) {
			await this.update({ clearing: false, error: `The imported trial balance could not be cleared: ${(cleared && cleared.error) || "no response"}` });
			return;
		}

		await this.update({ clearing: false, imported: null });
		if (onCleared) onCleared();
	};

	onOpenMapping = () => this.update({ mappingOpen: true });

	onMappingComplete = async () => {
		await this.update({ mappingOpen: false });
		await this.loadPreview({});
	};

	listAccountsToMap = () => {
		const { preview } = this.state;
		const unmapped = preview ? preview.unmapped || [] : [];
		return unmapped.map(account => ({ accountNumber: account.accountNumber || "", accountName: account.accountDescription || "", gifiCode: null }));
	};

	renderCurrentImport = () => {
		const { currentImport } = this.props;
		if (!currentImport || this.state.imported) return null;

		const balances = UtilityService.describeCount({ count: currentImport.count, singular: "balance" });
		return (
			<div className="tk-notice tbi__current" data-test="current-import">
				<span className="tk-notice__detail">
					This engagement's books are {balances} read from <strong>{currentImport.sourceFileName || "a trial balance"}</strong>. Reading
					another file replaces them.
				</span>
				<button type="button" className="tk-text-action tk-text-action--muted" disabled={this.state.clearing} onClick={this.onClearImport}>
					{this.state.clearing ? "Clearing…" : "Clear the imported trial balance"}
				</button>
			</div>
		);
	};

	renderColumnAssignment = () => {
		const { grid, columns, headerRowIndex } = this.state;
		if (!grid || grid.length === 0) return null;

		const foundAHeaderRow = typeof headerRowIndex === "number" && headerRowIndex >= 0;
		const headerRow = (foundAHeaderRow ? grid[headerRowIndex] : grid[0]) || [];
		const roleForColumn = columnIndex => {
			const assigned = Object.keys(columns || {}).find(role => columns[role] === columnIndex);
			return assigned || null;
		};

		return (
			<div className="tbi__columns" data-test="column-assignment">
				<p className="tbi__hint">
					These are the columns we understood. Correct any that are wrong before reading the figures.
				</p>
				{headerRow.map((heading, columnIndex) => (
					<div key={columnIndex} className="tbi__column-row">
						<span className="tbi__column-heading">{String(heading || "").trim() || `Column ${columnIndex + 1}`}</span>
						<ToolsSelect
							value={roleForColumn(columnIndex)}
							options={COLUMN_ROLES}
							onChange={role => this.onAssignColumn({ columnIndex, role })}
							placeholder="Not used"
							inline
						/>
					</div>
				))}
			</div>
		);
	};

	renderUnmapped = () => {
		const { preview } = this.state;
		const unmapped = preview.unmapped || [];
		if (unmapped.length === 0) return null;

		return (
			<div className="tbi__unmapped" data-test="import-unmapped">
				<p className="tbi__problem">
					{UtilityService.describeCount({ count: unmapped.length, singular: "account" })} on this file {unmapped.length === 1 ? "is" : "are"} not
					on the client's chart yet. Nothing is guessed for them — say which statement line each one reports on, and the file is read again.
				</p>
				<button type="button" className="tk-button tk-button--secondary" data-test="map-unmapped" onClick={this.onOpenMapping}>
					<Icon.Map size={14} /> Map these accounts
				</button>
				{unmapped.slice(0, 12).map((account, index) => (
					<div key={index} className="tbi__unmapped-row">
						<span>{account.accountNumber || "(no account number)"}</span>
						<span>{account.accountDescription || ""}</span>
						<strong>{UtilityService.formatCurrency(account.amount)}</strong>
					</div>
				))}
			</div>
		);
	};

	renderPreview = () => {
		const { preview } = this.state;
		if (!preview) return null;

		const unreadable = (preview.detected && preview.detected.unmatched) || [];

		return (
			<div className="tbi__preview" data-test="import-preview">
				{unreadable.length > 0 && (
					<p className="tbi__problem">{unreadable.join("; ")}.</p>
				)}

				<div className="tbi__figure-row">
					<span>Rows read</span>
					<strong>{preview.rowsRead}</strong>
				</div>
				<div className="tbi__figure-row">
					<span>Accounts that mapped</span>
					<strong>{preview.entries.length}</strong>
				</div>
				<div className="tbi__figure-row">
					<span>Debits</span>
					<strong>{UtilityService.formatCurrency(preview.debitTotal)}</strong>
				</div>
				<div className="tbi__figure-row">
					<span>Credits</span>
					<strong>{UtilityService.formatCurrency(preview.creditTotal)}</strong>
				</div>

				{preview.balances ? (
					<p className="tbi__balanced" data-test="import-balances">
						<Icon.Check size={14} /> The trial balance balances.
					</p>
				) : (
					<p className="tbi__problem" data-test="import-out-of-balance">
						Debits and credits are out by {UtilityService.formatCurrency(Math.abs(preview.difference))}. A trial
						balance that does not balance cannot be imported — the difference has to be found in the file.
					</p>
				)}

				{preview.balances && preview.unmapped.length === 0 && (
					<div className="tbi__commit">
						<button
							type="button"
							className="tk-button"
							data-test="import-commit"
							disabled={this.state.importing || !!this.state.imported}
							onClick={this.onImport}
						>
							{this.state.importing ? "Importing…" : "Use these figures for this engagement"}
						</button>
						<p className="tk-form-help">
							This replaces anything imported before, and can be undone. Nothing about the client's bookkeeping
							is changed.
						</p>
					</div>
				)}

				{this.state.imported && (
					<p className="tbi__balanced" data-test="import-done">
						<Icon.Check size={14} /> {this.state.imported.imported} balances imported
						{this.state.imported.replaced > 0 ? `, replacing ${this.state.imported.replaced} from a previous import` : ""}.
					</p>
				)}

				{this.renderUnmapped()}
			</div>
		);
	};

	render() {
		const { clientId } = this.props;
		const { reading, error, grid, fileName, mappingOpen } = this.state;

		if (mappingOpen) {
			return (
				<CoaMappingReview
					clientId={clientId}
					accounts={this.listAccountsToMap()}
					source="manual"
					title="Map the accounts on this file"
					subtitle="Each account is added to the client's chart under the statement line you confirm. The file is read again once they are all placed."
					hideSkip
					onComplete={this.onMappingComplete}
				/>
			);
		}

		return (
			<div className="tbi" data-test="trial-balance-import">
				{this.renderCurrentImport()}

				<p className="tbi__hint">
					Read a trial balance kept somewhere else, without going through bookkeeping. Nothing is written until
					you choose to use the figures — this shows what the file would produce so it can be corrected first.
				</p>

				<label className="tk-button tk-button--secondary tbi__choose">
					<Icon.Upload size={14} /> {fileName || "Choose a spreadsheet or CSV"}
					<input type="file" accept=".xlsx,.csv" data-test="trial-balance-file" onChange={this.onChooseFile} hidden />
				</label>

				{reading && <p className="tbi__hint">Reading…</p>}
				{error && <p className="tbi__problem" data-test="import-error">{error}</p>}

				{grid && this.renderColumnAssignment()}
				{this.renderPreview()}
			</div>
		);
	}
}

export default TrialBalanceImportPanel;
